import { ref, computed } from 'vue'
import { api } from '../services/api.js'

const TOKEN_KEY = 'access_token'
const USER_KEY = 'user'

const user = ref(null)
const loading = ref(false)
const error = ref(null)

function loadStoredAuth() {
  const storedUser = localStorage.getItem(USER_KEY)
  if (storedUser) {
    try {
      user.value = JSON.parse(storedUser)
    } catch {
      localStorage.removeItem(USER_KEY)
      localStorage.removeItem(TOKEN_KEY)
    }
  }
}

loadStoredAuth()

export function useAuth() {
  const isAuthenticated = computed(() => !!user.value && !!localStorage.getItem(TOKEN_KEY))

  const login = async (email, password) => {
    loading.value = true
    error.value = null
    try {
      const response = await api.post('/auth/login', { email, password })
      const { access_token, user: loggedInUser } = response.data
      localStorage.setItem(TOKEN_KEY, access_token)
      localStorage.setItem(USER_KEY, JSON.stringify(loggedInUser))
      user.value = loggedInUser
      return true
    } catch (err) {
      error.value = err.response?.data?.detail || err.message || 'Login failed'
      return false
    } finally {
      loading.value = false
    }
  }

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    user.value = null
  }

  const fetchMe = async () => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) return
    try {
      const response = await api.get('/auth/me')
      user.value = response.data
      localStorage.setItem(USER_KEY, JSON.stringify(response.data))
    } catch (err) {
      if (err.response?.status === 401) {
        logout()
      }
    }
  }

  const requestPasswordReset = async (email) => {
    loading.value = true
    error.value = null
    try {
      const response = await api.post('/auth/forgot-password', { email })
      return response.data
    } catch (err) {
      error.value = err.response?.data?.detail || err.message || 'Failed to request reset'
      throw err
    } finally {
      loading.value = false
    }
  }

  const resetPassword = async (email, resetToken, newPassword) => {
    loading.value = true
    error.value = null
    try {
      const response = await api.post('/auth/reset-password', {
        email,
        reset_token: resetToken,
        new_password: newPassword,
      })
      return response.data
    } catch (err) {
      error.value = err.response?.data?.detail || err.message || 'Failed to reset password'
      throw err
    } finally {
      loading.value = false
    }
  }

  const createUser = async (email, password, isAdmin = false) => {
    loading.value = true
    error.value = null
    try {
      const response = await api.post('/auth/users', {
        email,
        password,
        is_admin: isAdmin,
        is_active: true,
      })
      return response.data
    } catch (err) {
      error.value = err.response?.data?.detail || err.message || 'Failed to create user'
      throw err
    } finally {
      loading.value = false
    }
  }

  const listUsers = async () => {
    const response = await api.get('/auth/users')
    return response.data
  }

  return {
    user,
    loading,
    error,
    isAuthenticated,
    login,
    logout,
    fetchMe,
    requestPasswordReset,
    resetPassword,
    createUser,
    listUsers,
  }
}
